// Currency helper: maps current i18n language to display currency and converts
// CZK (canonical price) to that currency using ČNB rates from /api/fx/rates.
//
// Stripe checkout stays in CZK. This is display-only.

import { useEffect, useState, useCallback } from "react";
import { api } from "./api";

export const LANG_TO_CURRENCY = {
  cs: "CZK",
  sk: "EUR",
  hu: "HUF",
  en: "EUR",
};

export const CURRENCY_LOCALE = {
  CZK: "cs-CZ",
  EUR: "en-IE", // Irish English uses €1 234,56 style — fine for SK/EN/at-large
  HUF: "hu-HU",
  USD: "en-US",
};

const FX_CACHE_KEY = "ss_fx_rates_v1";
const FX_TTL_MS = 60 * 60 * 1000; // 1 hour client cache

let _ratesPromise = null;

export async function loadRates(force = false) {
  if (!force) {
    try {
      const cached = JSON.parse(localStorage.getItem(FX_CACHE_KEY) || "null");
      if (cached && cached.fetched_at && Date.now() - cached.fetched_at < FX_TTL_MS && cached.rates) {
        return cached;
      }
    } catch (e) { /* ignore */ }
  }
  if (_ratesPromise) return _ratesPromise;
  _ratesPromise = api.get("/fx/rates").then((r) => {
    const payload = { rates: r.data.rates || {}, fetched_at: Date.now(), lang_to_currency: r.data.lang_to_currency || LANG_TO_CURRENCY };
    try { localStorage.setItem(FX_CACHE_KEY, JSON.stringify(payload)); } catch (e) { /* ignore */ }
    _ratesPromise = null;
    return payload;
  }).catch(() => {
    _ratesPromise = null;
    return { rates: { CZK: 1 }, fetched_at: Date.now(), lang_to_currency: LANG_TO_CURRENCY };
  });
  return _ratesPromise;
}

export function convertFromCzk(amountCzk, currency, rates) {
  if (!amountCzk || !currency || currency === "CZK") return amountCzk || 0;
  const rate = rates && rates[currency];
  if (!rate || rate <= 0) return amountCzk;
  return amountCzk / rate; // rate = CZK per 1 unit of foreign currency
}

export function formatLocalized(amountCzk, lang, rates) {
  const currency = LANG_TO_CURRENCY[lang] || "CZK";
  const value = convertFromCzk(amountCzk, currency, rates);
  const locale = CURRENCY_LOCALE[currency] || "cs-CZ";
  const decimals = currency === "HUF" ? 0 : (currency === "CZK" ? 0 : 2);
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value || 0);
  } catch (e) {
    return `${Math.round(value)} ${currency}`;
  }
}

export function useFx() {
  const [rates, setRates] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(FX_CACHE_KEY) || "null");
      return cached?.rates || { CZK: 1 };
    } catch (e) { return { CZK: 1 }; }
  });
  useEffect(() => {
    loadRates().then((p) => setRates(p.rates || { CZK: 1 }));
  }, []);
  const format = useCallback((amountCzk, lang) => formatLocalized(amountCzk, lang, rates), [rates]);
  return { rates, format };
}
